import { useEffect, useState } from 'react';
import { isIOSWebViewGold } from '../../lib/platform';
import { getDetectionInfo, resetDetection } from '../../lib/webviewgold-detection';
import { getPrefetchState, getTimeSinceStart, retryTokenFetch, getTokenFromCache } from '../../lib/token-prefetch';
import { getCachedToken, clearTokenCache, getTokenAgeInDays } from '../../lib/token-cache';
import { getPushLogs, clearPushLogs, exportPushLogs } from '../../lib/push-diagnostics';
import { triggerFirebaseTokenFetch, checkWindowToken } from '../../lib/webviewgold-bridge';
import { getNotificationInitStatus } from '../../lib/notifications';
import { auth } from '../../lib/firebase';
import { X, RefreshCw, ChevronDown, ChevronUp, Copy, Trash2 } from 'lucide-react';

export function WebViewGoldDebug() {
  const [visible, setVisible] = useState(false);
  const [minimized, setMinimized] = useState(true);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    detection: true,
    token: true,
    diagnostics: false,
    logs: false
  });
  const [userAgentExpanded, setUserAgentExpanded] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const isAdmin = auth.currentUser?.email === 'breadflowbybadabec@gmail.com';

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const debugParam = urlParams.get('debugPush');
    if (debugParam === '1' || debugParam === 'true' || isAdmin) {
      setVisible(true);
    }
  }, [isAdmin]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshKey(prev => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const detectionInfo = getDetectionInfo();
  const prefetchState = getPrefetchState();
  const cachedTokenInfo = getCachedToken();
  const windowToken = checkWindowToken();
  const timeSinceStart = getTimeSinceStart();
  const notificationStatus = getNotificationInitStatus();
  const logs = getPushLogs();
  const tokenAge = getTokenAgeInDays();

  const handleManualTrigger = async () => {
    await triggerFirebaseTokenFetch();
    setRefreshKey(prev => prev + 1);
  };

  const handleRerunDetection = () => {
    resetDetection();
    setRefreshKey(prev => prev + 1);
  };

  const handleRetry = async () => {
    await retryTokenFetch();
    setRefreshKey(prev => prev + 1);
  };

  const handleClearCache = () => {
    clearTokenCache();
    setRefreshKey(prev => prev + 1);
  };

  const handleExportLogs = () => {
    const logsJson = exportPushLogs();
    navigator.clipboard.writeText(logsJson);
    alert('Logs copied to clipboard!');
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const maskToken = (token: string | null) => {
    if (!token) return 'null';
    return token.substring(0, 10) + '...' + token.substring(token.length - 4);
  };

  if (!visible) {
    return null;
  }

  const StateIndicator = ({ state }: { state: string }) => {
    const colors = {
      idle: 'bg-gray-400',
      fetching: 'bg-yellow-400',
      success: 'bg-green-500',
      failed: 'bg-red-500'
    };
    return <span className={`inline-block w-2 h-2 rounded-full ${colors[state as keyof typeof colors]}`} />;
  };

  return (
    <div className={`fixed bottom-4 right-4 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 ${minimized ? 'w-80' : 'w-96 max-h-[90vh] overflow-y-auto'}`}>
      <div className="sticky top-0 bg-white border-b border-gray-200 p-3 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-sm">iOS Push Debug</h3>
          {isIOSWebViewGold() && <StateIndicator state={prefetchState} />}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setMinimized(!minimized)}
            className="text-gray-500 hover:text-gray-700"
          >
            {minimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setVisible(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!minimized && (
        <div className="p-3 space-y-3 text-xs">
          <Section title="Detection Status" expanded={expandedSections.detection} onToggle={() => toggleSection('detection')}>
            <InfoRow label="User-Agent" value={
              <button onClick={() => setUserAgentExpanded(!userAgentExpanded)} className="text-left text-blue-600 hover:underline">
                {userAgentExpanded ? detectionInfo.userAgent : `${detectionInfo.userAgent.substring(0, 30)}...`}
              </button>
            } />
            <InfoRow label="WebViewGold" value={detectionInfo.hasWebViewGold ? '✅ Yes' : '❌ No'} />
            <InfoRow label="iOS Device" value={detectionInfo.isIOSDevice ? '✅ Yes' : '❌ No'} />
            <InfoRow label="Detection Result" value={detectionInfo.isIOSWebViewGold ? '✅ iOS WebViewGold' : '❌ Not detected'} />
            <InfoRow label="Method" value={detectionInfo.detectionMethod} />
          </Section>

          {isIOSWebViewGold() && (
            <>
              <Section title="Token Information" expanded={expandedSections.token} onToggle={() => toggleSection('token')}>
                <InfoRow label="window.firebaseplayerid" value={windowToken ? maskToken(windowToken) : '❌ Not found'} />
                <InfoRow label="Cached Token" value={cachedTokenInfo.token ? '✅ Available' : '❌ None'} />
                <InfoRow label="Token Source" value={cachedTokenInfo.source || 'N/A'} />
                {tokenAge !== null && <InfoRow label="Token Age" value={`${tokenAge.toFixed(2)} days`} />}
                <InfoRow label="Prefetch State" value={
                  <span className="flex items-center gap-1">
                    <StateIndicator state={prefetchState} />
                    {prefetchState}
                  </span>
                } />
                {timeSinceStart && <InfoRow label="Time Since Load" value={`${(timeSinceStart / 1000).toFixed(1)}s`} />}
              </Section>

              <Section title="Diagnostics" expanded={expandedSections.diagnostics} onToggle={() => toggleSection('diagnostics')}>
                <InfoRow label="Notification Init" value={notificationStatus.initialized ? '✅ Initialized' : '❌ Not initialized'} />
                <InfoRow label="Init Error" value={notificationStatus.error || 'None'} />
                <InfoRow label="Total Logs" value={logs.length} />
              </Section>

              <Section title="Recent Logs" expanded={expandedSections.logs} onToggle={() => toggleSection('logs')}>
                <div className="space-y-1 max-h-48 overflow-y-auto bg-gray-50 p-2 rounded font-mono text-xs">
                  {logs.slice(-10).reverse().map((log, idx) => (
                    <div key={idx} className="text-xs">
                      <span className="text-gray-500">{new Date(log.timestamp).toLocaleTimeString()}</span>
                      <span className={`ml-2 ${log.level === 'error' ? 'text-red-600' : log.level === 'success' ? 'text-green-600' : 'text-gray-700'}`}>
                        [{log.category}] {log.message}
                      </span>
                    </div>
                  ))}
                </div>
              </Section>

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleManualTrigger}
                  className="px-2 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center justify-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" />
                  Trigger Fetch
                </button>
                <button
                  onClick={handleRerunDetection}
                  className="px-2 py-1.5 bg-purple-500 text-white rounded hover:bg-purple-600 flex items-center justify-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" />
                  Re-detect
                </button>
                <button
                  onClick={handleRetry}
                  className="px-2 py-1.5 bg-orange-500 text-white rounded hover:bg-orange-600 flex items-center justify-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" />
                  Retry
                </button>
                <button
                  onClick={handleClearCache}
                  className="px-2 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 flex items-center justify-center gap-1"
                >
                  <Trash2 className="w-3 h-3" />
                  Clear Cache
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleExportLogs}
                  className="px-2 py-1.5 bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
                >
                  <Copy className="w-3 h-3" />
                  Export Logs
                </button>
                <button
                  onClick={() => {
                    clearPushLogs();
                    setRefreshKey(prev => prev + 1);
                  }}
                  className="px-2 py-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center justify-center gap-1"
                >
                  <Trash2 className="w-3 h-3" />
                  Clear Logs
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, expanded, onToggle, children }: { title: string; expanded: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div className="border border-gray-200 rounded">
      <button
        onClick={onToggle}
        className="w-full px-2 py-1.5 bg-gray-50 hover:bg-gray-100 flex justify-between items-center"
      >
        <span className="font-semibold text-xs">{title}</span>
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {expanded && <div className="p-2 space-y-1">{children}</div>}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-gray-600">{label}:</span>
      <span className="font-mono text-gray-900">{value}</span>
    </div>
  );
}
